import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import AppsIcon from '@mui/icons-material/Apps';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { PublicCategoryGroup } from './publicApi';
import { CategoryIcon } from './components';

export const EXPLORE_TYPES = ['all', 'biz', 'prod', 'job'] as const;
export type ExploreType = typeof EXPLORE_TYPES[number];

export interface ExploreFilters {
  bookableOnline: boolean;
  hasShop: boolean;
  hasJobs: boolean;
}
export type ExploreFilterKey = keyof ExploreFilters;

/** Group code for a group or leaf code; products and pills only know the group. */
export function groupOf(groups: PublicCategoryGroup[], code: string | undefined) {
  if (!code) return undefined;
  return groups.find((group) => group.code === code || group.leaves.some((leaf) => leaf.code === code));
}

export function categoryName(groups: PublicCategoryGroup[], code: string | undefined) {
  const group = groupOf(groups, code);
  if (!group) return undefined;
  return group.code === code ? group.name : group.leaves.find((leaf) => leaf.code === code)?.name;
}

/**
 * All / Businesses / Products / Jobs. A segmented control on wide screens; on phones it shrinks to
 * icons and only the selected segment keeps its label (see .hub-type-switch in public.css).
 */
export function TypeSwitch({ value, onChange }: { value: ExploreType; onChange: (type: ExploreType) => void }) {
  const { t } = useTranslation();
  const items: [ExploreType, string, typeof AppsIcon][] = [
    ['all', t('hub.explore.all', 'All'), AppsIcon],
    ['biz', t('hub.explore.businesses', 'Businesses'), StorefrontOutlinedIcon],
    ['prod', t('hub.explore.products', 'Products'), LocalMallOutlinedIcon],
    ['job', t('hub.explore.jobs', 'Jobs'), WorkOutlineIcon],
  ];
  return <div className="hub-type-switch" role="radiogroup" aria-label={t('hub.explore.show', 'Show')}>
    {items.map(([code, label, Icon]) => <button key={code} type="button" role="radio" aria-checked={value === code} title={label}
      onClick={() => onChange(code)}>
      <Icon sx={{ fontSize: 18 }} aria-hidden /><span>{label}</span>
    </button>)}
  </div>;
}

export function FilterChips({ filters, onToggle }: { filters: ExploreFilters; onToggle: (key: ExploreFilterKey) => void }) {
  const { t } = useTranslation();
  const items: [ExploreFilterKey, string][] = [
    ['bookableOnline', t('hub.search.bookable', 'Book online')],
    ['hasShop', t('hub.search.shop', 'Has a shop')],
    ['hasJobs', t('hub.search.openJobs', 'Hiring now')],
  ];
  return <>
    {items.map(([key, label]) => <button key={key} type="button" className="hub-filter-chip" aria-pressed={filters[key]} onClick={() => onToggle(key)}>{label}</button>)}
  </>;
}

/**
 * Category picker. Wide screens show one row of group chips (as many as fit) plus "More" for the
 * rest and the leaves; phones get one button that opens the same list as a bottom sheet, because a sideways
 * scrolling chip row is hard to use with a thumb.
 */
export function CategoryPicker({ groups, value, onChange, filters, onToggleFilter, onClear, disabled, open, onOpenChange }: {
  groups: PublicCategoryGroup[];
  value: string | undefined;
  onChange: (code: string | undefined) => void;
  filters: ExploreFilters;
  onToggleFilter: (key: ExploreFilterKey) => void;
  /** Drops the category and every filter in one URL update. */
  onClear: () => void;
  /** Jobs have no directory category; the picker stays visible but inert. */
  disabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const activeGroup = groupOf(groups, value)?.code;
  const selectedName = categoryName(groups, value);
  const activeFilters = Object.values(filters).filter(Boolean).length;
  const chipLabel = (group: PublicCategoryGroup) => (group.code === activeGroup && value !== group.code ? selectedName : group.name);

  // One row only: as many groups as fit, the rest stay reachable through "More". Widths come from
  // an invisible ruler row that always holds every chip, so labels and language changes re-measure.
  const row = useRef<HTMLDivElement>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(groups.length);
  useLayoutEffect(() => {
    const container = row.current;
    const measure = ruler.current;
    if (!container || !measure) return undefined;
    const compute = () => {
      const style = getComputedStyle(container);
      const gap = parseFloat(style.columnGap) || 0;
      const available = container.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
      const [allChip, ...chips] = Array.from(measure.children) as HTMLElement[];
      const moreChip = chips.pop();
      let used = (allChip?.offsetWidth ?? 0) + gap + (moreChip?.offsetWidth ?? 0);
      let count = 0;
      for (const chip of chips) {
        if (used + gap + chip.offsetWidth > available) break;
        used += gap + chip.offsetWidth;
        count += 1;
      }
      setFit(count);
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [groups, selectedName, t]);

  // The selected group always stays visible, taking the last slot if it would be cut off.
  let shown = groups.slice(0, fit);
  const active = groups.find((group) => group.code === activeGroup);
  if (active && fit > 0 && !shown.includes(active)) shown = [...shown.slice(0, fit - 1), active];
  const hasMore = shown.length < groups.length || groups.some((group) => group.leaves.length > 0);

  return <>
    <div className="hub-cat-chips" ref={row} role="group" aria-label={t('hub.landing.categories', 'Categories')}>
      <div className="hub-cat-ruler" ref={ruler} aria-hidden>
        <span className="hub-cat-chip">{t('hub.explore.allCategories', 'All categories')}</span>
        {groups.map((group) => <span key={group.code} className="hub-cat-chip"><CategoryIcon name={group.icon} size={17} />{chipLabel(group)}</span>)}
        <span className="hub-cat-chip more">{t('hub.explore.more', 'More')} <ExpandMoreIcon sx={{ fontSize: 17 }} /></span>
      </div>
      <button type="button" className="hub-cat-chip" aria-pressed={!value} disabled={disabled} onClick={() => onChange(undefined)}>
        {t('hub.explore.allCategories', 'All categories')}
      </button>
      {shown.map((group) => <button key={group.code} type="button" className="hub-cat-chip" data-group={group.code}
        aria-pressed={activeGroup === group.code} disabled={disabled} onClick={() => onChange(activeGroup === group.code && value === group.code ? undefined : group.code)}>
        <CategoryIcon name={group.icon} size={17} />
        {chipLabel(group)}
      </button>)}
      {hasMore && <button type="button" className="hub-cat-chip more" disabled={disabled} onClick={() => onOpenChange(true)}
        aria-haspopup="dialog">
        {t('hub.explore.more', 'More')} <ExpandMoreIcon sx={{ fontSize: 17 }} aria-hidden />
      </button>}
    </div>

    <button type="button" className="hub-cat-trigger" aria-haspopup="dialog" aria-expanded={open} onClick={() => onOpenChange(true)}>
      <TuneIcon sx={{ fontSize: 18 }} aria-hidden />
      <span className="label">{!disabled && selectedName ? selectedName : t('hub.explore.filters', 'Filters')}</span>
      {(activeFilters > 0 || (!disabled && value)) && <span className="count">{activeFilters + (value && !disabled ? 1 : 0)}</span>}
    </button>

    {open && <CategorySheet onClose={() => onOpenChange(false)} title={t('hub.explore.filters', 'Filters')} onClear={onClear}>
      <section className="hub-sheet-section">
        <h3>{t('hub.explore.refine', 'Show only')}</h3>
        <div className="hub-sheet-chips"><FilterChips filters={filters} onToggle={onToggleFilter} /></div>
      </section>
      {!disabled && <section className="hub-sheet-section">
        <h3>{t('hub.landing.categories', 'Categories')}</h3>
        <button type="button" className="hub-sheet-group" aria-pressed={!value} onClick={() => { onChange(undefined); onOpenChange(false); }}>
          <span className="hub-group-icon" aria-hidden><AppsIcon sx={{ fontSize: 19 }} /></span>{t('hub.explore.allCategories', 'All categories')}
        </button>
        {groups.map((group) => <div key={group.code} className="hub-sheet-block">
          <button type="button" className="hub-sheet-group" data-group={group.code} aria-pressed={value === group.code}
            onClick={() => { onChange(group.code); onOpenChange(false); }}>
            <span className="hub-group-icon" aria-hidden><CategoryIcon name={group.icon} size={19} /></span>{group.name}
          </button>
          {group.leaves.length > 0 && <div className="hub-sheet-chips">
            {group.leaves.map((leaf) => <button key={leaf.code} type="button" className={`hub-chip${value === leaf.code ? ' on' : ''}`}
              aria-pressed={value === leaf.code} onClick={() => { onChange(leaf.code); onOpenChange(false); }}>{leaf.name}</button>)}
          </div>}
        </div>)}
      </section>}
    </CategorySheet>}
  </>;
}

/** Bottom sheet on phones, centred panel on wider screens. Escape and the backdrop close it. */
function CategorySheet({ title, onClose, onClear, children }: { title: string; onClose: () => void; onClear: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('button')?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, []);

  return <div className="hub-sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={panel} className="hub-sheet" role="dialog" aria-modal="true" aria-label={title}>
      <header className="hub-sheet-head">
        <h2>{title}</h2>
        <button type="button" className="hub-icon-btn" aria-label={t('hub.explore.close', 'Close')} onClick={onClose}><CloseIcon sx={{ fontSize: 20 }} aria-hidden /></button>
      </header>
      <div className="hub-sheet-body">{children}</div>
      <footer className="hub-sheet-foot">
        <button type="button" className="hub-btn ghost" onClick={onClear}>{t('hub.explore.clear', 'Clear all')}</button>
        <button type="button" className="hub-btn" onClick={onClose}>{t('hub.explore.showResults', 'Show results')}</button>
      </footer>
    </div>
  </div>;
}
